/**
 * Shown when a left column has nothing left to decide (`isShareDecided`).
 * "card" sits in one kid's left column; "review" heads the whole filtered
 * review. The burst plays only when the state flips to cleared while the
 * page is open, so a page that loads already cleared stays calm.
 */
import { CheckCircleFilled } from "@ant-design/icons"
import { motion, useReducedMotion } from "motion/react"
import { useState } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import { useThemeContext } from "#contexts/ThemeContextProvider"

import { COMMENT_HISTORY_SIMILAR } from "./reviewCardLayout"

const BURST_DOTS = 8

/** True from the render where `cleared` turns on until it turns off. */
export const useJustCleared = (cleared: boolean): boolean => {
  const [previous, setPrevious] = useState(cleared)
  const [just, setJust] = useState(false)
  if (cleared !== previous) {
    setPrevious(cleared)
    setJust(cleared)
  }
  return just
}

const Burst = ({ radius, colors }: { radius: number; colors: string[] }) => (
  <span
    aria-hidden
    style={{
      position: "absolute",
      left: "50%",
      top: "50%",
      width: 0,
      height: 0,
      pointerEvents: "none",
    }}
  >
    {Array.from({ length: BURST_DOTS }, (_, index) => {
      const angle = (index / BURST_DOTS) * Math.PI * 2
      return (
        <motion.span
          key={index}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius,
            opacity: 0,
            scale: 0.4,
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            position: "absolute",
            left: -3,
            top: -3,
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: colors[index % colors.length],
          }}
        />
      )
    })}
  </span>
)

const ClearedCelebration = ({
  size,
  title,
  detail,
  burst,
}: {
  size: "card" | "review"
  title: string
  detail?: string
  burst: boolean
}) => {
  const theme = useThemeContext()
  const reduceMotion = useReducedMotion()
  const review = size === "review"
  const iconSize = review ? 28 : 18
  return (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.leftSm}
      gap={review ? 12 : 8}
      style={{
        alignItems: "center",
        padding: review ? "14px 16px" : "10px 12px",
        borderRadius: 8,
        border: `1px solid ${theme.eric.green}55`,
        backgroundColor: `${theme.eric.green}12`,
        color: theme.eric.green,
        minWidth: 0,
      }}
    >
      <div
        data-testid={review ? "sr-review-cleared" : "sr-left-cleared"}
        data-burst={burst ? "true" : "false"}
        style={{
          display: "flex",
          alignItems: "center",
          gap: review ? 12 : 8,
          minWidth: 0,
        }}
      >
        <span
          style={{
            position: "relative",
            display: "inline-flex",
            flex: "0 0 auto",
          }}
        >
          {burst && !reduceMotion ? (
            <Burst
              radius={review ? 34 : 22}
              colors={[
                theme.eric.green,
                COMMENT_HISTORY_SIMILAR,
                theme.eric.blue,
              ]}
            />
          ) : null}
          <motion.span
            initial={burst && !reduceMotion ? { scale: 0.4 } : false}
            animate={{ scale: [0.4, 1.15, 1] }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            style={{ display: "inline-flex", fontSize: iconSize }}
          >
            <CheckCircleFilled />
          </motion.span>
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: review ? 16 : 13,
              lineHeight: review ? "22px" : "18px",
              fontWeight: 600,
            }}
          >
            {title}
          </div>
          {detail ? (
            <div
              style={{
                fontSize: 12,
                lineHeight: "16px",
                color: theme.black,
                opacity: 0.7,
              }}
            >
              {detail}
            </div>
          ) : null}
        </div>
      </div>
    </AnimateInFlex>
  )
}

export default ClearedCelebration
